import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState("");

  const handleClose = (newOpen: boolean) => {
    if (!newOpen) {
      onOpenChange(false);
      setTimeout(() => setConfirmText(""), 300); // reset text after transition
    } else {
      onOpenChange(true);
    }
  };

  const confirmWord = t(
    "profile.delete_account.confirm_word",
    "CONFIRM",
  ).toUpperCase();
  const isConfirmValid = confirmText.trim().toUpperCase() === confirmWord;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-md w-[calc(100%-32px)] rounded-[20px] p-6 border-none shadow-xl gap-0 outline-none"
      >
        {/* Close Button at top-right */}
        <DialogClose className="absolute right-4 top-4 rounded-md p-1 outline-none focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors border-2 border-transparent bg-[#F9F9FA] text-black hover:bg-gray-200">
          <X className="w-[18px] h-[18px] stroke-[2.5]" />
        </DialogClose>

        {/* Icon */}
        <div className="flex justify-center mb-4 mt-2">
          <div className="w-[64px] h-[64px] rounded-[20px] bg-[#FEF3F2] flex items-center justify-center">
            <Trash2 className="w-8 h-8 text-[#F04438]" strokeWidth={1.5} />
          </div>
        </div>

        <DialogHeader className="space-y-3 px-2">
          <DialogTitle className="text-[20px] font-bold text-center text-[#101828]">
            {t("profile.delete_account.title", "Are you sure?")}
          </DialogTitle>
          <DialogDescription className="text-[14px] leading-[1.6] text-[#475467]">
            {t(
              "profile.delete_account.desc1",
              "We are very sorry to see you leaving. Deleting your account will permanently delete all of the data plus any active subscriptions and this action can't be undone!",
            )}
            <br />
            {t(
              "profile.delete_account.desc2",
              "If you still want to delete you account, enter ",
            )}
            <span className="font-bold text-[#101828]">
              "{t("profile.delete_account.confirm_word", "CONFIRM")}"
            </span>
            {t("profile.delete_account.desc3", " to proceed")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 px-1 w-full flex flex-col gap-4">
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t("profile.delete_account.confirm_word", "CONFIRM")}
            className="h-[48px] text-center font-bold text-[16px] rounded-[10px] border-[#D0D5DD] focus-visible:ring-0 focus-visible:border-[#F04438] placeholder:font-normal placeholder:text-[#98A2B3]"
          />

          <Button
            type="button"
            disabled={!isConfirmValid}
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
            className="w-full rounded-[10px] h-[48px] bg-[#F04438] hover:bg-[#D92D20] disabled:bg-[#F9A19A] disabled:opacity-100 font-semibold text-white transition-colors text-[16px] shadow-sm"
          >
            {t("profile.delete_account.action", "Delete account")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
